import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  MdEdit,
  MdAssignment,
  MdFastForward,
  MdSchedule,
  MdDescription,
} from "react-icons/md";
import { Tooltip } from "antd";
import { CustomFeature } from "core/features/maps/typings";
import { OverlayView } from "@react-google-maps/api";
import { CurrentViewTypeEnum, PointsBaseMap } from "./typings";
import { MarkersContainer, ContextMenu, ContextMenuItem } from "./styles";
import useModal from "../../../hooks/useModalPlanejamentoOperacoes";
import { GeometryType } from "./typings";
import useMapData from "../../../hooks/useMapData";
import { MapInfo } from "../../MapInfo";
import OrdemServico from "../../OrdemServico";
import { useFilters } from "core/features/filters/store";
import { MapInfoSemaforo } from "../../MapInfoSemaforo";
import GestaoOperacoesLote from "../../GestaoOperacoesLote";
import { useGlobalLoading } from "core/features/global-loading";
import { useValidatePermission } from "core/features/auth/hooks/useValidatePermission";
import {
  getMatchedWindowsForPlanos,
  WINDOW_OVERLAP_COLOR,
} from "../../FiltroAcompanhamentoOperacional/utils";
import { useTalhaoBoxSelect } from "./hooks/useTalhaoBoxSelect";
import { buildTalhaoBoundsIndex, TalhaoBounds } from "./hooks/boxSelectUtils";
import BoxSelectOverlay from "./components/BoxSelectOverlay";
import { useViewportTalhaoIds } from "./hooks/useViewportTalhaoIds";
import {
  ExecucaoTalhaoMarker,
  getPlanoTalhaoDisponiveis,
} from "./ExecucaoTalhaoMarker";

const SELECTED_STYLE = {
  fillOpacity: 0.8,
  strokeColor: "#FF0000",
  strokeWeight: 3,
  zIndex: 1000,
};

/** Normalize feature ids so Set/Map lookups stay consistent (number vs string). */
function getFeatureTalhaoId(feature: google.maps.Data.Feature): string | null {
  const id = feature.getProperty("id");
  if (id == null || id === "") return null;
  return String(id);
}

interface BaseMapProps {
  mapRef: any;
  displayMarkers: boolean;
  hideMarkers: () => void;
  restoreMarkers: () => void;
  currentViewType: CurrentViewTypeEnum;
  points?: PointsBaseMap;
  talhoes?: string[];
  planoOperacionalData?: any;
  zoom: number;
  heavyOverlaysEnabled?: boolean;
  zoomAllowsMarkers?: boolean;
}

function BaseMap({
  mapRef,
  displayMarkers,
  currentViewType,
  points,
  planoOperacionalData,
  zoom,
  heavyOverlaysEnabled = true,
  zoomAllowsMarkers = true,
}: BaseMapProps) {
  const selectedTalhoesRef = useRef<Set<string>>(new Set());
  const selectedFeaturesRef = useRef<Map<string, google.maps.Data.Feature>>(
    new Map(),
  );
  const featuresByIdRef = useRef<Map<string, google.maps.Data.Feature>>(new Map());
  const boundsByIdRef = useRef<Map<string, TalhaoBounds>>(new Map());
  const styleFnRef = useRef<
    | ((feature: google.maps.Data.Feature) => google.maps.Data.StyleOptions)
    | null
  >(null);
  const {
    setListTalhoesIds,
    listTalhoesIds,
    indicadoresDeDia,
    justificativaAtraso,
    ordemServico,
    rangeDates,
    numberRepeted,
    rangeDatesDeleted,
    semOS,
    arrayColorsAndDates,
    rangeWindows,
    janelaExecucao,
    statusSemaforo,
  } = useMapData();

  const {
    filters: { menuName, filtersAdvanced },
  } = useFilters();
  const { classeOperacao } = filtersAdvanced;

  const [popupPosition, setPopupPosition] =
    useState<google.maps.LatLngLiteral | null>(null);
  const [isOrdemServicoOpen, setIsOrdemServicoOpen] = useState(false);
  const [isGestaoOperacaoOpen, setIsGestaoOperacaoOpen] = useState(false);
  const [action, setAction] = useState<
    "antecipar" | "prorrogar" | "justificar"
  >();
  const [popupTalhao, setPopupTalhao] = useState<CustomFeature | null>(null);
  /** Snapshot used by the context menu so items match the live selection on first open. */
  const [menuSelectionCount, setMenuSelectionCount] = useState(0);
  const [contextMenuKey, setContextMenuKey] = useState(0);
  const isContextMenuOpenRef = useRef(false);
  const { hasPermission } = useValidatePermission();

  const closeContextMenu = useCallback(() => {
    isContextMenuOpenRef.current = false;
    setPopupPosition(null);
    setPopupTalhao(null);
    setMenuSelectionCount(0);
  }, []);

  const stopMenuPropagation = useCallback(
    (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
    },
    [],
  );

  const stopMenuEvent = useCallback(
    (event: { preventDefault: () => void; stopPropagation: () => void }) => {
      event.preventDefault();
      event.stopPropagation();
    },
    [],
  );

  const {
    setIsOpen,
    setCode,
    setTalhaoId,
    setIds,
    showLegends,
    setPlanejamentoOperacionalPorTalhao,
  } = useModal();
  const { isLoading } = useGlobalLoading();

  const Polygons = useMemo(
    () =>
      points?.features.filter(
        (feature) =>
          feature.geometry.type === GeometryType.Polygon ||
          feature.geometry.type === GeometryType.MultiPolygon,
      ) || [],
    [points?.features],
  );

  const visibleTalhaoIds = useViewportTalhaoIds(
    mapRef,
    Polygons,
    zoomAllowsMarkers,
  );

  const visiblePolygons = useMemo(() => {
    if (!visibleTalhaoIds) return Polygons;
    return Polygons.filter((item) =>
      visibleTalhaoIds.has(String(item.properties?.id ?? "")),
    );
  }, [Polygons, visibleTalhaoIds]);

  const zoomScale = menuName === "Mapa Semáforo" ? zoom / 20 : 1;
  const planoTalhaoDisponiveis = getPlanoTalhaoDisponiveis(
    popupTalhao?.planosoperacionais!,
    rangeDatesDeleted,
    semOS,
  );

  const searchAtrasados = useCallback((plano: any) => {
    return plano?.some((p: any) => p.StatusSemaforo === 3);
  }, []);

  const selectedCount =
    popupPosition != null ? menuSelectionCount : listTalhoesIds.size;

  const showEditPlanejamento = selectedCount <= 1;
  const showAbrirOrdemServico =
    selectedCount > 0 && menuName === "Mapa Cronograma";
  const showGestaoLote =
    menuName === "Mapa Cronograma" &&
    (classeOperacao?.length ?? 0) > 0 &&
    selectedCount > 1;
  const showJustificarAtraso =
    menuName === "Mapa Semáforo" &&
    statusSemaforo.atrasado &&
    searchAtrasados(planoTalhaoDisponiveis) &&
    (classeOperacao?.length ?? 0) > 0;

  // Abrir OS / gestão em lote usam listTalhoesIds — não dependem de planos do popup.
  const showContextMenu =
    !!popupPosition &&
    !!popupTalhao &&
    (showAbrirOrdemServico ||
      showGestaoLote ||
      (showEditPlanejamento && planoTalhaoDisponiveis.length > 0) ||
      (showJustificarAtraso && planoTalhaoDisponiveis.length > 0));

  // Keep the imperative flag aligned with the visible menu. A stale "open" flag
  // swallows every subsequent map click after ctrl+box / right-click.
  useEffect(() => {
    isContextMenuOpenRef.current = showContextMenu;
  }, [showContextMenu]);

  const styleRefreshRafRef = useRef<number | null>(null);

  const scheduleSelectionStyleRefresh = useCallback(() => {
    if (styleRefreshRafRef.current != null) return;

    styleRefreshRafRef.current = window.requestAnimationFrame(() => {
      styleRefreshRafRef.current = null;
      const map = mapRef.current;
      if (!map) return;

      // Clear stale overrides, then re-evaluate styles from `selected` property once.
      map.data.revertStyle();
      if (styleFnRef.current) {
        map.data.setStyle(styleFnRef.current);
        return;
      }
      const currentStyle = map.data.getStyle();
      if (typeof currentStyle === "function") {
        map.data.setStyle(currentStyle);
      }
    });
  }, [mapRef]);

  const clearSelectedOnAllFeatures = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    map.data.forEach((feature: google.maps.Data.Feature) => {
      try {
        if (feature.getProperty("selected")) {
          feature.setProperty("selected", false);
        }
      } catch {
        // Feature may have been removed on GeoJSON reload.
      }
    });
  }, [mapRef]);

  const clearSelectionStyles = useCallback(() => {
    clearSelectedOnAllFeatures();
    selectedFeaturesRef.current.clear();
    selectedTalhoesRef.current.clear();
    scheduleSelectionStyleRefresh();
  }, [clearSelectedOnAllFeatures, scheduleSelectionStyleRefresh]);

  const syncSelectionList = useCallback(() => {
    setListTalhoesIds(new Set(selectedTalhoesRef.current));
  }, [setListTalhoesIds]);

  const openContextMenuAt = useCallback(
    (
      latLng: google.maps.LatLng,
      preferredFeature?: google.maps.Data.Feature | null,
    ) => {
      const menuNameAtual = useFilters.getState().filters.menuName;
      let hasExistingSelection = selectedTalhoesRef.current.size > 0;

      if (
        preferredFeature &&
        menuNameAtual === "Mapa Cronograma" &&
        !hasExistingSelection
      ) {
        const clickedTalhaoId = getFeatureTalhaoId(preferredFeature);
        if (clickedTalhaoId) {
          preferredFeature.setProperty("selected", true);
          selectedTalhoesRef.current.add(clickedTalhaoId);
          selectedFeaturesRef.current.set(clickedTalhaoId, preferredFeature);
          scheduleSelectionStyleRefresh();
          hasExistingSelection = true;
        }
      }

      syncSelectionList();
      const selectionCount = selectedTalhoesRef.current.size;
      if (selectionCount === 0) return;

      setMenuSelectionCount(selectionCount);
      setPopupPosition({
        lat: latLng.lat(),
        lng: latLng.lng(),
      });
      setContextMenuKey((key) => key + 1);

      let targetFeature = preferredFeature ?? null;
      if (selectionCount === 1) {
        const selectedId = Array.from(selectedTalhoesRef.current)[0];
        targetFeature =
          selectedFeaturesRef.current.get(selectedId) ??
          preferredFeature ??
          null;
      } else if (!targetFeature) {
        const selectedId = Array.from(selectedTalhoesRef.current)[0];
        targetFeature = selectedFeaturesRef.current.get(selectedId) ?? null;
      }

      if (!targetFeature) {
        const fallbackId = Array.from(selectedTalhoesRef.current)[0];
        setPopupTalhao({ id: fallbackId } as unknown as CustomFeature);
        if (fallbackId) setTalhaoId(fallbackId);
        return;
      }

      const talhaoId = getFeatureTalhaoId(targetFeature);
      const code = targetFeature.getProperty("codigo");
      const planosoperacionais = targetFeature
        .getProperty("planosoperacionais")
        ?.map((plano: any) => plano?.PlanoOperacionalOperacaoTalhaoId);

      const props: Record<string, unknown> = {};
      try {
        targetFeature.forEachProperty((value: unknown, key: string) => {
          props[key] = value;
        });
      } catch {
        if (talhaoId) props.id = talhaoId;
        if (code) props.codigo = code;
      }

      setPopupTalhao(props as unknown as CustomFeature);
      if (code) setCode(code);
      if (talhaoId) setTalhaoId(talhaoId);
      if (planosoperacionais?.length > 0) setIds(planosoperacionais);
    },
    [
      scheduleSelectionStyleRefresh,
      setCode,
      setIds,
      setTalhaoId,
      syncSelectionList,
    ],
  );

  const handleBoxSelect = useCallback(
    (talhaoIds: string[]) => {
      if (!mapRef.current || talhaoIds.length === 0) return;

      const featuresById = featuresByIdRef.current;
      for (const talhaoId of talhaoIds) {
        const feature = featuresById.get(talhaoId);
        if (!feature) continue;

        const style = styleFnRef.current?.(feature);
        if (
          !style ||
          style.fillColor === "transparent" ||
          style.fillOpacity === 0
        ) {
          continue;
        }

        if (selectedTalhoesRef.current.has(talhaoId)) {
          feature.setProperty("selected", false);
          selectedTalhoesRef.current.delete(talhaoId);
          selectedFeaturesRef.current.delete(talhaoId);
        } else {
          feature.setProperty("selected", true);
          selectedTalhoesRef.current.add(talhaoId);
          selectedFeaturesRef.current.set(talhaoId, feature);
        }
      }
      scheduleSelectionStyleRefresh();
      syncSelectionList();
      setMenuSelectionCount(selectedTalhoesRef.current.size);
    },
    [mapRef, scheduleSelectionStyleRefresh, syncSelectionList],
  );

  const { boxRect, skipNextClickRef, armSkipNextClick, getProjection } =
    useTalhaoBoxSelect({
      mapRef,
      boundsByIdRef,
      enabled: true,
      onSelect: handleBoxSelect,
    });

  /** Run a context-menu action without letting the click hit the map underneath. */
  const runContextMenuAction = useCallback(
    (actionFn: () => void) => {
      armSkipNextClick();
      syncSelectionList();
      actionFn();
      closeContextMenu();
    },
    [armSkipNextClick, closeContextMenu, syncSelectionList],
  );

  function clearSelection() {
    clearSelectionStyles();
    setListTalhoesIds(new Set());
    closeContextMenu();
  }

  const buildStyleFn = useCallback(() => {
    return (feature: any) => {
      const stroke = feature.getProperty("stroke");
      const strokeWidth = 2;
      const strokeOpacity = Number(feature.getProperty("strokeOpacity")) || 1;
      const code = feature.getProperty("codigo");

      let fill = feature.getProperty("fill");
      const fillOpacity = Number(feature.getProperty("fillOpacity"));
      const planosoperacionais = feature.getProperty("planosoperacionais");
      const plano = getPlanoTalhaoDisponiveis(
        planosoperacionais,
        rangeDatesDeleted,
        semOS,
      );

      if (menuName === "Mapa Cronograma" || menuName === "Mapa Semáforo") {
        const isSelected = feature.getProperty("selected");

        if (isSelected) {
          return {
            fillColor: feature.getProperty("fill"),
            ...SELECTED_STYLE,
          };
        }
      }

      if (menuName === "Mapa Cronograma") {
        if (janelaExecucao) {
          const planos = getPlanoTalhaoDisponiveis(
            feature.getProperty("planosoperacionais"),
            rangeDatesDeleted,
            semOS,
          );

          const matchedWindows = getMatchedWindowsForPlanos(
            planos,
            rangeWindows,
          );

          if (matchedWindows.length === 0) {
            fill = "transparent";
          } else if (matchedWindows.length === 1) {
            fill = matchedWindows[0].color;
          } else {
            fill = WINDOW_OVERLAP_COLOR;
          }
        } else {
          if (rangeDates.length > 0) {
            rangeDates.forEach((item) => {
              if (item.codigo === code) {
                fill = item.color;
              }
            });
          }

          if (numberRepeted.includes(code)) {
            fill =
              plano?.length <= 1
                ? rangeDates?.find((f) => f.codigo === code)?.color
                : feature.getProperty("fill");
          }

          const dataProgramada = feature
            .getProperty("planosoperacionais")
            ?.flatMap((p: any) => p.DataProgramada);
          rangeDatesDeleted.forEach((item) => {
            if (
              item.codigo === code &&
              dataProgramada?.includes(item.dateOriginal)
            ) {
              fill = "transparent";
            }
          });

          if (
            feature
              .getProperty("planosoperacionais")
              ?.every(
                (p: any) =>
                  p.PossuiOSAberta === true && p.PossuiOSAberta === semOS,
              )
          ) {
            fill = "transparent";
          } else if (
            feature
              .getProperty("planosoperacionais")
              ?.some(
                (p: any) =>
                  p.PossuiOSAberta === true && p.PossuiOSAberta === semOS,
              )
          ) {
            if (arrayColorsAndDates.length > 0) {
              arrayColorsAndDates.forEach((item) => {
                if (
                  feature
                    .getProperty("planosoperacionais")
                    ?.some(
                      (p: any) =>
                        p.DataProgramada?.slice(0, 10) === item.dataProgramada,
                    )
                ) {
                  const counter = getPlanoTalhaoDisponiveis(
                    feature.getProperty("planosoperacionais"),
                    rangeDatesDeleted,
                    semOS,
                  );
                  if (counter?.length === 1) {
                    fill = item.color;
                  } else if (counter?.length > 1) {
                    fill = feature.getProperty("fill");
                  } else {
                    fill = "transparent";
                  }
                }
              });
            }
          }
        }
      }

      if (!fill) {
        return {
          strokeColor: stroke || "#000",
          strokeWeight: strokeWidth,
          strokeOpacity: strokeOpacity,
          fillOpacity: 0,
        };
      }

      return {
        strokeColor: stroke || "#000",
        strokeWeight: strokeWidth,
        strokeOpacity: strokeOpacity,
        fillColor: fill,
        fillOpacity: isNaN(fillOpacity) ? 0.6 : fillOpacity,
      };
    };
  }, [
    menuName,
    janelaExecucao,
    rangeDatesDeleted,
    semOS,
    rangeWindows,
    rangeDates,
    numberRepeted,
    arrayColorsAndDates,
  ]);

  // Reload polygons only when map data changes — do not wipe selection on pan/zoom re-renders.
  useEffect(() => {
    if (!mapRef.current || !points) return;

    const map = mapRef.current;

    if (styleRefreshRafRef.current != null) {
      window.cancelAnimationFrame(styleRefreshRafRef.current);
      styleRefreshRafRef.current = null;
    }
    selectedFeaturesRef.current.clear();
    selectedTalhoesRef.current.clear();
    setListTalhoesIds(new Set());
    isContextMenuOpenRef.current = false;
    setPopupPosition(null);
    setPopupTalhao(null);
    setMenuSelectionCount(0);

    map.data.forEach((f: any) => map.data.remove(f));
    map.data.addGeoJson(points as any);

    const nextFeatures = new Map<string, google.maps.Data.Feature>();
    map.data.forEach((feature: google.maps.Data.Feature) => {
      const id = getFeatureTalhaoId(feature);
      if (id) nextFeatures.set(id, feature);
    });
    featuresByIdRef.current = nextFeatures;
    boundsByIdRef.current = buildTalhaoBoundsIndex(
      (points.features ?? []) as Parameters<typeof buildTalhaoBoundsIndex>[0]
    );

    const styleFn = buildStyleFn();
    styleFnRef.current = styleFn;
    map.data.setStyle(styleFn);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points]);

  // Style/filter updates keep current selection and rebind live feature refs.
  useEffect(() => {
    if (!mapRef.current || !points) return;

    const map = mapRef.current;
    const styleFn = buildStyleFn();
    styleFnRef.current = styleFn;

    const nextFeatures = new Map<string, google.maps.Data.Feature>();
    map.data.forEach((feature: google.maps.Data.Feature) => {
      const id = getFeatureTalhaoId(feature);
      const shouldSelect = !!(id && selectedTalhoesRef.current.has(id));
      try {
        feature.setProperty("selected", shouldSelect);
      } catch {
        // Feature may have been removed on GeoJSON reload.
      }
      if (shouldSelect && id) {
        nextFeatures.set(id, feature);
      }
    });
    selectedFeaturesRef.current = nextFeatures;

    map.data.revertStyle();
    map.data.setStyle(styleFn);
  }, [buildStyleFn, points]);

  useEffect(() => {
    if (!mapRef.current || !points) return;

    const map = mapRef.current;

    const clickListener = map.data.addListener("click", (event: any) => {
      // Ignore only the synthetic click that follows a ctrl+box gesture.
      if (skipNextClickRef.current) {
        skipNextClickRef.current = false;
        return;
      }

      // Close a visible context menu, but still process this click as a selection.
      if (isContextMenuOpenRef.current) {
        closeContextMenu();
      }

      const feature = event.feature;
      const talhaoId = getFeatureTalhaoId(feature);

      const style = styleFnRef.current?.(feature);

      if (!style?.fillColor || style.fillColor === "transparent") {
        return;
      }

      if (!talhaoId) {
        clearSelectionStyles();
        setListTalhoesIds(new Set());
        closeContextMenu();
        return;
      }

      const isCtrlPressed = event.domEvent?.ctrlKey;
      const alreadySelected = selectedTalhoesRef.current.has(talhaoId);

      if (!isCtrlPressed) {
        if (alreadySelected) {
          clearSelectionStyles();
          setListTalhoesIds(new Set());
          setMenuSelectionCount(0);
          return;
        }

        clearSelectedOnAllFeatures();
        selectedFeaturesRef.current.clear();
        selectedTalhoesRef.current.clear();

        feature.setProperty("selected", true);
        selectedTalhoesRef.current.add(talhaoId);
        selectedFeaturesRef.current.set(talhaoId, feature);

        scheduleSelectionStyleRefresh();
        syncSelectionList();
        setMenuSelectionCount(1);
        return;
      }

      if (alreadySelected) {
        feature.setProperty("selected", false);
        selectedTalhoesRef.current.delete(talhaoId);
        selectedFeaturesRef.current.delete(talhaoId);
      } else {
        feature.setProperty("selected", true);
        selectedTalhoesRef.current.add(talhaoId);
        selectedFeaturesRef.current.set(talhaoId, feature);
      }

      scheduleSelectionStyleRefresh();
      syncSelectionList();
      setMenuSelectionCount(selectedTalhoesRef.current.size);
    });

    return () => {
      google.maps.event.removeListener(clickListener);
    };
  }, [
    points,
    clearSelectionStyles,
    clearSelectedOnAllFeatures,
    scheduleSelectionStyleRefresh,
    syncSelectionList,
    skipNextClickRef,
    setListTalhoesIds,
    closeContextMenu,
  ]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (isOrdemServicoOpen || isGestaoOperacaoOpen) return;
      const mapElement = document.getElementById("map-print");
      if (!mapElement) return;
      if (!mapElement.contains(e.target as Node)) {
        clearSelection();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrdemServicoOpen, isGestaoOperacaoOpen]);

  useEffect(() => {
    if (!points) return;

    const container = document.getElementById("map-print-area");
    if (!container) return;

    function handleContextMenu(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.("[data-talhao-context-menu]")) return;

      event.preventDefault();
      event.stopPropagation();

      const map = mapRef.current;
      const projection = getProjection();
      if (!map || !projection) return;

      const mapDiv = map.getDiv() as HTMLElement;
      const rect = mapDiv.getBoundingClientRect();
      const latLng = projection.fromContainerPixelToLatLng(
        new google.maps.Point(
          event.clientX - rect.left,
          event.clientY - rect.top,
        ),
      );
      if (!latLng) return;

      // After ctrl+box (or any multi/single selection), open from current selection.
      // Do not depend on Google Maps data rightclick — it is unreliable after box-select.
      if (selectedTalhoesRef.current.size > 0) {
        openContextMenuAt(latLng, null);
        return;
      }

      let hitFeature: google.maps.Data.Feature | null = null;
      map.data.forEach((feature: google.maps.Data.Feature) => {
        if (hitFeature) return;
        if (!getFeatureTalhaoId(feature)) return;
        const geometry = feature.getGeometry();
        if (!geometry) return;

        const type = geometry.getType();
        if (type === "Polygon") {
          const ring = (geometry as google.maps.Data.Polygon).getAt(0);
          const polygon = new google.maps.Polygon({ paths: ring.getArray() });
          if (google.maps.geometry.poly.containsLocation(latLng, polygon)) {
            hitFeature = feature;
          }
          return;
        }

        if (type === "MultiPolygon") {
          const multi = geometry as google.maps.Data.MultiPolygon;
          const matched = multi
            .getArray()
            .some((poly: google.maps.Data.Polygon) => {
              const outer = poly.getAt(0);
              const polygon = new google.maps.Polygon({
                paths: outer.getArray(),
              });
              return google.maps.geometry.poly.containsLocation(
                latLng,
                polygon,
              );
            });
          if (matched) hitFeature = feature;
        }
      });

      if (!hitFeature) {
        closeContextMenu();
        return;
      }

      openContextMenuAt(latLng, hitFeature);
    }

    container.addEventListener("contextmenu", handleContextMenu, true);
    return () => {
      container.removeEventListener("contextmenu", handleContextMenu, true);
    };
  }, [points, getProjection, openContextMenuAt, closeContextMenu, mapRef]);

  return (
    <>
      <BoxSelectOverlay boxRect={boxRect} />
      {isOrdemServicoOpen && (
        <OrdemServico
          planoOperacionalData={planoOperacionalData}
          onClose={() => {
            skipNextClickRef.current = false;
            clearSelectionStyles();
            setIsOrdemServicoOpen(false);
          }}
        />
      )}

      {isGestaoOperacaoOpen && action && (
        <GestaoOperacoesLote
          action={action}
          planoOperacionalData={planoOperacionalData}
          onClose={() => {
            skipNextClickRef.current = false;
            setIsGestaoOperacaoOpen(false);
          }}
        />
      )}

      {showLegends && <MapInfo />}
      {showLegends && <MapInfoSemaforo />}
      <MarkersContainer>
        {visiblePolygons.map((item, index) => (
          <ExecucaoTalhaoMarker
            key={
              item.properties?.id ||
              item.properties?.codigo ||
              `talhao-${index}`
            }
            item={item}
            currentViewType={currentViewType}
            displayMarkers={displayMarkers}
            heavyOverlaysEnabled={heavyOverlaysEnabled}
            menuName={menuName}
            janelaExecucao={janelaExecucao}
            rangeDatesDeleted={rangeDatesDeleted}
            semOS={semOS}
            rangeWindows={rangeWindows}
            numberRepeted={numberRepeted}
            isLoading={isLoading}
            ordemServico={ordemServico ?? undefined}
            justificativaAtraso={justificativaAtraso ?? undefined}
            statusSemaforo={statusSemaforo}
            indicadoresDeDia={indicadoresDeDia}
            zoomScale={zoomScale}
          />
        ))}

        {showContextMenu && (
          <OverlayView
            key={contextMenuKey}
            position={popupPosition!}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <ContextMenu
              data-talhao-context-menu
              onMouseDown={stopMenuPropagation}
              onMouseUp={stopMenuPropagation}
              onClick={stopMenuPropagation}
              onContextMenu={stopMenuEvent}
              onPointerDown={stopMenuPropagation}
            >
              {showEditPlanejamento && (
                <ContextMenuItem
                  onClick={(e) => {
                    stopMenuPropagation(e);
                    runContextMenuAction(() => {
                      setPlanejamentoOperacionalPorTalhao(popupTalhao);
                      setIsOpen(true);
                    });
                  }}
                >
                  <MdEdit size={20} /> Editar Planejamento
                </ContextMenuItem>
              )}
              {showAbrirOrdemServico && (
                <Tooltip
                  title={
                    !hasPermission(["OrdemServico.Cadastrar"])
                      ? " Você não tem permissão para abrir ordem de serviço"
                      : undefined
                  }
                >
                  <span style={{ display: "block" }}>
                    <ContextMenuItem
                      disabled={!hasPermission(["OrdemServico.Cadastrar"])}
                      onClick={(e) => {
                        stopMenuPropagation(e);
                        if (!hasPermission(["OrdemServico.Cadastrar"])) return;
                        runContextMenuAction(() => {
                          setIsOrdemServicoOpen(true);
                        });
                      }}
                    >
                      <MdAssignment size={20} /> Abrir Ordem de Serviço
                    </ContextMenuItem>
                  </span>
                </Tooltip>
              )}
              {showGestaoLote && (
                <>
                  <Tooltip
                    title={
                      !hasPermission(["MapaCronograma.AnteciparOperacao"])
                        ? " Você não tem permissão para antecipar a operação"
                        : undefined
                    }
                  >
                    <span style={{ display: "block" }}>
                      <ContextMenuItem
                        disabled={
                          !hasPermission(["MapaCronograma.AnteciparOperacao"])
                        }
                        onClick={(e) => {
                          stopMenuPropagation(e);
                          if (
                            !hasPermission(["MapaCronograma.AnteciparOperacao"])
                          )
                            return;
                          runContextMenuAction(() => {
                            setAction("antecipar");
                            setIsGestaoOperacaoOpen(true);
                          });
                        }}
                      >
                        <MdFastForward size={20} /> Antecipar Operação
                      </ContextMenuItem>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !hasPermission(["MapaCronograma.ProrrogarOperacao"])
                        ? " Você não tem permissão para prorrogar a operação"
                        : undefined
                    }
                  >
                    <span style={{ display: "block" }}>
                      <ContextMenuItem
                        disabled={
                          !hasPermission(["MapaCronograma.ProrrogarOperacao"])
                        }
                        onClick={(e) => {
                          stopMenuPropagation(e);
                          if (
                            !hasPermission(["MapaCronograma.ProrrogarOperacao"])
                          )
                            return;
                          runContextMenuAction(() => {
                            setAction("prorrogar");
                            setIsGestaoOperacaoOpen(true);
                          });
                        }}
                      >
                        <MdSchedule size={20} /> Prorrogar Operação
                      </ContextMenuItem>
                    </span>
                  </Tooltip>
                </>
              )}
              {showJustificarAtraso && (
                <Tooltip
                  title={
                    !hasPermission(["MapaSemaforo.JustificarAtrasos"])
                      ? "Você não tem permissão para justificar atraso"
                      : undefined
                  }
                >
                  <span style={{ display: "inline-block" }}>
                    <ContextMenuItem
                      disabled={
                        !hasPermission(["MapaSemaforo.JustificarAtrasos"])
                      }
                      onClick={(e) => {
                        stopMenuPropagation(e);
                        if (!hasPermission(["MapaSemaforo.JustificarAtrasos"]))
                          return;
                        runContextMenuAction(() => {
                          setAction("justificar");
                          setIsGestaoOperacaoOpen(true);
                        });
                      }}
                    >
                      <MdDescription size={20} /> Justificar Atraso
                    </ContextMenuItem>
                  </span>
                </Tooltip>
              )}
            </ContextMenu>
          </OverlayView>
        )}
      </MarkersContainer>
    </>
  );
}

export default BaseMap;
